import { FilterOptionType } from '@repo/common/components/Filter';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useUpdateSecodaEntity, type ISecodaEntity } from '../../api';
import { resourceCatalogQueryKeyFactory } from '../../api/hooks/resourceCatalog/constants';
import {
	CreatedAtRender,
	LastUpdatedRender,
	OwnerRender,
	TitleRender,
} from '../../components/TableV2/render';
import { useGenericActions } from '../../components/TableV2/SecodaEntity.hooks';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

export const useColumns = (): ExtendedDataTableColumn<ISecodaEntity>[] => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateDashboardSecodaEntity } = useUpdateSecodaEntity({
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.allLists(),
				});
			},
		},
	});

	const handleChangeUserOwners = useCallback(
		(id: string) => (owners: string[]) => {
			updateDashboardSecodaEntity({
				data: {
					id,
					owners,
				},
			});
		},
		[updateDashboardSecodaEntity]
	);

	const handleChangeGroupOwners = useCallback(
		(id: string) => (owners: string[]) => {
			updateDashboardSecodaEntity({
				data: {
					id,
					owners_groups: owners,
				},
			});
		},
		[updateDashboardSecodaEntity]
	);

	const columns: ExtendedDataTableColumn<ISecodaEntity>[] = useMemo(
		() => [
			{
				accessor: 'title',
				titleLabel: 'Name',
				render: (record: ISecodaEntity) => (
					<TitleRender record={record} showDescription />
				),
				filterOptionType: FilterOptionType.TITLE,
			},
			{
				accessor: 'created_at',
				titleLabel: 'Created',
				render: (record) => <CreatedAtRender record={record} />,
			},
			{
				accessor: 'updated_at',
				titleLabel: 'Updated',
				render: (record) => (
					<LastUpdatedRender record={record} field="updated_at" />
				),
			},
			{
				accessor: 'owners',
				titleLabel: 'Owners',
				render: (record) => (
					<OwnerRender
						record={
							{
								id: record.id,
								owners: record.owners,
								owners_groups: record.owners_groups,
							} as ISecodaEntity
						}
						field="owners"
						onChangeUserOwners={handleChangeUserOwners}
						onChangeGroupOwners={handleChangeGroupOwners}
					/>
				),
				navigate: false,
			},
		],
		[handleChangeUserOwners, handleChangeGroupOwners]
	);

	return columns;
};

export const useActions = () => {
	const genericActions = useGenericActions();
	const actions = useMemo(
		() => [
			...genericActions.filter((action) => action.id === 'actions::delete'),
		],
		[genericActions]
	);
	return actions;
};
